import React, { useEffect, useState } from 'react';
import client from '../api/client';

function VersionCard() {
  const [ver, setVer]     = useState(null);
  const [bumping, setBumping] = useState(false);
  const [msg, setMsg]     = useState('');

  const load = () =>
    client.get('/security/version').then(r => setVer(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const bump = async () => {
    setBumping(true);
    try {
      const r = await client.post('/security/version/bump');
      setMsg(r.data.message);
      load();
      setTimeout(() => setMsg(''), 5000);
    } catch { setMsg('Failed'); }
    finally { setBumping(false); }
  };

  return (
    <div className="card mt-4">
      <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
        Force Client Refresh
      </div>
      <div className="card-body">
        {msg && <div className="alert alert-success py-2 mb-3" style={{ fontSize: '13px' }}>{msg}</div>}
        <p style={{ fontSize: '13px', color: '#495057' }}>
          When you deploy a new build, users with the app open in their browser won't automatically
          get the new code — the old JavaScript is cached. Clicking below bumps the app version
          number. Every open browser tab will detect the change within the configured poll interval
          (set under Configuration → Global Settings) and show a
          <strong> "New version deployed — refresh now"</strong> banner.
        </p>
        {ver && (
          <div className="mb-3" style={{ fontSize: '13px' }}>
            <span className="text-muted">Current version: </span>
            <strong style={{ fontFamily: 'monospace' }}>v{ver.version}</strong>
            {ver.updated_at && (
              <span className="text-muted ml-2" style={{ fontSize: '11px' }}>
                (bumped {new Date(ver.updated_at).toLocaleString('en-GB', { timeZone: 'Europe/London' })})
              </span>
            )}
          </div>
        )}
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={bump}
          disabled={bumping}
        >
          <i className="bi bi-arrow-repeat mr-1" />
          {bumping ? 'Bumping…' : 'Bump Version → Prompt All Users to Refresh'}
        </button>
      </div>
    </div>
  );
}

const PRIMARY = '#B71530';

export default function AppAdminMaintenance() {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({ maintenance: false, message: '' });
  const [msg, setMsg]         = useState({ text: '', type: 'success' });

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: 'success' }), 4000);
  };

  const fetchStatus = () => {
    setLoading(true);
    client.get('/maintenance/status')
      .then(r => {
        setStatus(r.data);
        setForm({ maintenance: r.data.maintenance, message: r.data.message || '' });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await client.patch('/maintenance/', form);
      showMsg(r.data.message);
      fetchStatus();
    } catch (err) {
      showMsg(err?.response?.data?.detail || 'Failed to update', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const toggleNow = async (on) => {
    setSaving(true);
    try {
      const r = await client.patch('/maintenance/', { maintenance: on, message: form.message });
      showMsg(r.data.message);
      fetchStatus();
    } catch (err) {
      showMsg(err?.response?.data?.detail || 'Failed', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '640px' }}>
      {msg.text && (
        <div className={`alert alert-${msg.type} py-2 mb-3`} style={{ fontSize: '14px' }}>
          {msg.text}
        </div>
      )}

      {/* Status banner */}
      {!loading && status && (
        <div
          className={`alert ${status.maintenance ? 'alert-danger' : 'alert-success'} mb-4`}
          style={{ fontSize: '14px' }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <strong>{status.maintenance ? '⚠ System is UNDER MAINTENANCE' : '✓ System is LIVE'}</strong>
              {status.updated_at && (
                <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
                  Last changed: {new Date(status.updated_at).toLocaleString('en-GB', { timeZone: 'Europe/London' })}
                  {status.updated_by && ` by ${status.updated_by}`}
                </div>
              )}
            </div>
            <button
              className={`btn btn-sm ${status.maintenance ? 'btn-success' : 'btn-danger'}`}
              onClick={() => toggleNow(!status.maintenance)}
              disabled={saving}
            >
              {saving ? '…' : status.maintenance ? 'Bring Back Online' : 'Enable Maintenance'}
            </button>
          </div>
        </div>
      )}

      {/* Settings form */}
      <div className="card">
        <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
          Maintenance Settings
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-danger" /></div>
          ) : (
            <form onSubmit={handleSave}>
              {/* Toggle */}
              <div className="mb-4">
                <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>
                  Maintenance Mode
                </label>
                <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${!form.maintenance ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setForm(p => ({ ...p, maintenance: false }))}
                  >
                    ✓ System Live
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${form.maintenance ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setForm(p => ({ ...p, maintenance: true }))}
                  >
                    ⚠ Maintenance
                  </button>
                </div>
                <div className="form-text mt-2" style={{ fontSize: '12px' }}>
                  When maintenance mode is <strong>on</strong>, all users except the App Admin are redirected
                  to a maintenance page. The API returns 503 for non-admin requests.
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>
                  Message shown to users
                </label>
                <textarea
                  className="form-control form-control-sm"
                  rows={3}
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="The system is currently undergoing scheduled maintenance. Please check back shortly."
                />
              </div>

              <button
                type="submit"
                className="btn btn-sm"
                style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </form>
          )}
        </div>
      </div>

      <VersionCard />

      {/* Preview */}
      {form.maintenance && (
        <div className="card mt-3" style={{ border: '1px dashed #dee2e6' }}>
          <div className="card-header" style={{ fontSize: '12px', color: '#6c757d', backgroundColor: '#f8f9fa' }}>
            Preview — what users will see
          </div>
          <div className="card-body text-center py-4">
            <i className="bi bi-tools" style={{ fontSize: '40px', color: '#6c757d', display: 'block', marginBottom: '12px' }} />
            <h5 style={{ color: '#343a40' }}>System Maintenance</h5>
            <p style={{ color: '#6c757d', fontSize: '14px', maxWidth: '380px', margin: '0 auto' }}>
              {form.message || 'The system is currently undergoing scheduled maintenance.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
